import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CustomCard from '../../../components/card/CustomCard'
import CustomCardHeader from '../../../components/card/CustomCardHeader'
import CardBody from '../../../components/card/CardBody'
import CustomTable, { TableRow, TableHeader, TableCell } from '../../../components/table/CustomTable'
import Pagination from '../../../components/Pagination'
import CustomTableItemLoader from '../../../components/CustomTableItemLoader'
import {
  tryFetchPoints,
  setFilters,
  loading,
} from '../../../redux/slices/rewardPointsSlice'
import { isoToLocaleFormat } from '../../../helpers/dateTimeHelper'
import { fixTo2Place } from '../../../helpers/helperFunctions'

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'POSTED', label: 'Posted' },
  { value: 'CANCEL', label: 'Cancelled' },
]

const GIVEN_FOR_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'PURCHASE_OF_GOODS', label: 'Purchase' },
  { value: 'USER_SIGNUP', label: 'Signup' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'FIRST_PURCHASE', label: 'First Purchase' },
  { value: 'REVIEW', label: 'Review' },
]

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'JEEVEE', label: 'Jeevee' },
  { value: 'SALT', label: 'SALT' },
]

// Nepal is UTC+5:45. Converts a date-only string ("YYYY-MM-DD") to a UTC ISO timestamp.
const nepalDateToUTC = (dateStr, isEndOfDay = false) => {
  if (!dateStr) return ''
  const [year, month, day] = dateStr.split('-').map(Number)
  const h = isEndOfDay ? 23 : 0
  const m = isEndOfDay ? 59 : 0
  const s = isEndOfDay ? 59 : 0
  const utcMs = Date.UTC(year, month - 1, day, h, m, s) - 345 * 60 * 1000
  return new Date(utcMs).toISOString().replace(/\.\d+Z$/, 'Z')
}

const StatusBadge = ({ status }) => {
  const colors = {
    DRAFT: 'bg-yellow-100 text-yellow-800',
    POSTED: 'bg-green-100 text-green-800',
    CANCEL: 'bg-red-100 text-red-800',
  }
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  )
}

export default function TransactionsList() {
  const dispatch = useDispatch()
  const { data, total_results, page, total_pages, loading, filters, error } = useSelector((state) => state.rewardPoints)
  const debounceRef = useRef(null)

  const fetchPoints = () => {
    const params = {
      page,
      per_page: 10,
      ...(filters.source_ref && { source_ref: filters.source_ref }),
      ...(filters.phone_number && { phone_number: filters.phone_number }),
      ...(filters.status && { status: filters.status }),
      ...(filters.given_for && { given_for: filters.given_for }),
      ...(filters.source_system && { source_system: filters.source_system }),
      ...(filters.created_from && { created_from: nepalDateToUTC(filters.created_from, false) }),
      ...(filters.created_to && { created_to: nepalDateToUTC(filters.created_to, true) }),
    }
    dispatch(tryFetchPoints(params))
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(fetchPoints, 500)
    return () => clearTimeout(debounceRef.current)
  }, [page, filters.source_ref, filters.phone_number, filters.status, filters.given_for, filters.source_system, filters.created_from, filters.created_to])

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }))
  }

  const handlePageChange = ({ selected }) => {
    dispatch(setFilters({ page: selected + 1 }))
  }

  const totalPages = total_pages || Math.ceil(total_results / 10)

  return (
    <div className="px-3 md:px-8 h-auto -mt-24">
      <div className="container mx-auto max-w-full">
        <div className="grid grid-cols-1 px-4 mb-16">
          <CustomCard>
            <CustomCardHeader color="blue" contentPosition="none">
              <div className="flex data-center justify-between w-full">
                <h2 className="text-2xl text-white">All Points</h2>
              </div>
            </CustomCardHeader>
            <CardBody>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <input
                  type="text"
                  placeholder="Source Ref (e.g. order-1001)"
                  value={filters.source_ref}
                  onChange={(e) => handleFilterChange('source_ref', e.target.value)}
                  className="border rounded px-3 py-2 text-sm text-rand-100 focus:outline-none focus:ring-2 focus:ring-secondary-500 w-52"
                />
                <input
                  type="text"
                  placeholder="Phone Number"
                  value={filters.phone_number}
                  onChange={(e) => handleFilterChange('phone_number', e.target.value)}
                  className="border rounded px-3 py-2 text-sm text-rand-100 focus:outline-none focus:ring-2 focus:ring-secondary-500 w-40"
                />
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="border rounded px-3 py-2 text-sm text-rand-100 focus:outline-none focus:ring-2 focus:ring-secondary-500"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={filters.given_for}
                  onChange={(e) => handleFilterChange('given_for', e.target.value)}
                  className="border rounded px-3 py-2 text-sm text-rand-100 focus:outline-none focus:ring-2 focus:ring-secondary-500"
                >
                  {GIVEN_FOR_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={filters.source_system}
                  onChange={(e) => handleFilterChange('source_system', e.target.value)}
                  className="border rounded px-3 py-2 text-sm text-rand-100 focus:outline-none focus:ring-2 focus:ring-secondary-500"
                >
                  {SOURCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div className="flex items-center gap-0 border rounded overflow-hidden">
                  <span className="px-3 py-2 text-xs text-rand-200 bg-gray-50 border-r whitespace-nowrap">Date range</span>
                  <input
                    type="date"
                    value={filters.created_from}
                    onChange={(e) => handleFilterChange('created_from', e.target.value)}
                    className="px-3 py-2 text-sm text-rand-100 focus:outline-none focus:ring-2 focus:ring-secondary-500 border-r"
                  />
                  <span className="px-2 text-xs text-rand-200 bg-white">–</span>
                  <input
                    type="date"
                    value={filters.created_to}
                    onChange={(e) => handleFilterChange('created_to', e.target.value)}
                    className="px-3 py-2 text-sm text-rand-100 focus:outline-none focus:ring-2 focus:ring-secondary-500"
                  />
                </div>
              </div>

              <CustomTable>
                <thead>
                  <TableRow>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Points</TableHeader>
                    <TableHeader>Given For</TableHeader>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Maturity Date</TableHeader>
                    <TableHeader>Source Ref</TableHeader>
                    <TableHeader>Source</TableHeader>
                    <TableHeader>Created Date</TableHeader>
                  </TableRow>
                </thead>
                {loading && data.length === 0 ? (
                  <CustomTableItemLoader
                    loading={true}
                    error={error}
                    isEmpty={false}
                    count={10}
                    colSpan={8}
                    key="reward-transactions"
                    retry={fetchPoints}
                    emptyStateMessage="No point entries found"
                    emptyStateIcon="receipt_long"
                  />
                ) : error ? (
                  <CustomTableItemLoader
                    loading={false}
                    error={error}
                    isEmpty={false}
                    count={10}
                    colSpan={8}
                    key="reward-transactions"
                    retry={fetchPoints}
                    emptyStateMessage="No point entries found"
                    emptyStateIcon="receipt_long"
                  />
                ) : data.length === 0 ? (
                  <CustomTableItemLoader
                    loading={false}
                    error={error}
                    isEmpty={true}
                    count={10}
                    colSpan={8}
                    key="reward-transactions"
                    retry={fetchPoints}
                    emptyStateMessage="No point entries found"
                    emptyStateIcon="receipt_long"
                  />
                ) : (
                  <tbody>
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={8} className="py-2">
                          <div className="flex data-center justify-center gap-2">
                            <div className="animate-pulse flex space-x-1">
                              <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                              <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                              <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                            </div>
                            <span className="text-sm text-gray-500">Loading...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    {data.map((tx) => (
                      <TableRow key={tx.id} className={loading ? 'opacity-50' : ''}>
                        <TableCell>
                          <StatusBadge status={tx.status} />
                        </TableCell>
                        <TableCell>
                          <span className={`text-sm font-semibold ${tx.point > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.point > 0 ? '+' : ''}{fixTo2Place(tx.point)}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-rand-100">{tx.given_for || '-'}</TableCell>
                        <TableCell>
                          <div className="text-sm text-rand-100">{tx.user?.number || '-'}</div>
                          <div className="text-xs text-rand-200">{tx.user?.name || ''}</div>
                        </TableCell>
                        <TableCell className="text-sm text-rand-100">{tx.maturity_date ? isoToLocaleFormat(tx.maturity_date) : '-'}</TableCell>
                        <TableCell className="text-sm text-rand-100">{tx.source_ref || '-'}</TableCell>
                        <TableCell>
                          <span className="text-sm text-rand-100">{tx.source_system || '-'}</span>
                        </TableCell>
                        <TableCell className="text-sm text-rand-200">
                          {tx.created_at ? isoToLocaleFormat(tx.created_at) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                )}
              </CustomTable>

              {!error && data.length > 0 && totalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    pageCount={totalPages}
                    onPageChange={handlePageChange}
                    forcePage={page - 1}
                  />
                  <p className="text-center text-xs text-rand-200 mt-2">
                    Showing {((page - 1) * 10) + 1}-
                    {Math.min(page * 10, total_results)} of {total_results} results
                  </p>
                </div>
              )}
            </CardBody>
          </CustomCard>
        </div>
      </div>
    </div>
  )
}
